/**
 * OCR利用状況の表示用ユーティリティ（#3146）
 *
 * サイドバーと企業アカウント画面（/company）で同じ表示を出すための共通処理。
 * 残高の算出式は運営側の企業詳細（CompanyDetail.tsx）と揃えている。
 *
 * 超過額（消費額 − 月間枠）は表示しない（#3205）。「今月の利用実績」と「枠」を
 * 両方出しているため引き算で導けるうえ、「超過」という語が未払い・ペナルティに読まれ、
 * クレジットから引かれていないという誤解を招いていたため。
 */

/** 表示に必要な会社の利用状況（画面ごとに型が異なるため必要な値だけを受け取る） */
export type OcrUsageSource = {
  /** 月間枠（円） */
  ocrMonthlyBudget: number;
  /** 当月消費額（円・枠超過分を含む累計） */
  currentMonthOcrCost: number;
  /** 当月読取枚数 */
  currentMonthOcrCount: number;
  /** OCRクレジット残高（円・マイナスになりうる） */
  ocrCreditBalance: number;
};

/** 画面に出す文言と、色分けの判定に使う値 */
export type OcrUsageDisplay = {
  /** 月間OCRの値（例: `残高 4,400円 / 枠5,000円`） */
  monthlyOcrText: string;
  /** 今月の読取の値（例: `12枚 / 600円`） */
  monthlyReadText: string;
  /** OCRクレジットの値（例: `残高 5,000円` / `残高 -400円`）。月間OCRと揃えて「残高」を値側に持たせる */
  creditBalanceText: string;
  /** クレジット残高がマイナスか（警告色の判定に使う） */
  isNegativeCredit: boolean;
};

/**
 * 月間枠の残高を算出する。
 * 消費額が枠を超えても 0 止まりとし、マイナスにはしない。
 */
export const calculateMonthlyOcrBalance = (
  ocrMonthlyBudget: number,
  currentMonthOcrCost: number,
): number => Math.max(0, ocrMonthlyBudget - currentMonthOcrCost);

/**
 * OCR利用状況の表示文言を生成する。
 *
 * 残高（円）と読取枚数（枚）は単位が異なり混同されやすいため、別々の文言として返す。
 * 呼び出し側で行を分けて表示すること。
 */
export const buildOcrUsageDisplay = (source: OcrUsageSource): OcrUsageDisplay => {
  const budget = source.ocrMonthlyBudget ?? 0;
  const cost = source.currentMonthOcrCost ?? 0;
  const count = source.currentMonthOcrCount ?? 0;
  const creditBalance = source.ocrCreditBalance ?? 0;

  const balance = calculateMonthlyOcrBalance(budget, cost);

  return {
    monthlyOcrText: `残高 ${balance.toLocaleString()}円 / 枠${budget.toLocaleString()}円`,
    monthlyReadText: `${count.toLocaleString()}枚 / ${cost.toLocaleString()}円`,
    creditBalanceText: `残高 ${creditBalance.toLocaleString()}円`,
    isNegativeCredit: creditBalance < 0,
  };
};
